import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { updateListingPricing } from "../../lib/stripe.js";
import { isListingPricingComplete } from "../../lib/listingPricing.js";
import { toUserFacingBillingError } from "../../lib/userFacingErrors.js";

export default function ListingPricingPanel({ listing, connectReady, onSaved }) {
  const { user } = useAuth();
  const [priceDollars, setPriceDollars] = useState("");
  const [pricingMode, setPricingMode] = useState("oneTime");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const pricingComplete = isListingPricingComplete(listing);

  useEffect(() => {
    const price = Number(listing?.price) || 0;
    setPriceDollars(price > 0 ? String(price) : "");
    setPricingMode(
      listing?.pricingMode === "subscription" ? "subscription" : "oneTime",
    );
    setError(null);
    setSaved(false);
  }, [listing?.id, listing?.price, listing?.pricingMode]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSaved(false);

    const price = Number(priceDollars);
    if (!Number.isFinite(price) || price < 0.5) {
      setError("Enter a valid price (minimum $0.50 CAD, before tax).");
      return;
    }

    if (!user?.$id) {
      setError("Sign in again to save price.");
      return;
    }

    setSaving(true);
    try {
      const { listing: updated } = await updateListingPricing(listing.id, {
        price,
        pricingMode,
      });
      onSaved?.(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(
        toUserFacingBillingError(err?.message ?? String(err), {
          fallback: "Could not save price. Please try again.",
        }),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <h3 className="text-base font-semibold tracking-[-0.15px] text-hc-text">
          Pricing
        </h3>
        <p className="mt-1 text-xs text-hc-text-dim">Sales tax is added at checkout.</p>
      </div>

      {!pricingComplete && (
        <p className="rounded-[12px] border border-hc-warning/30 bg-hc-warning-muted px-3 py-2.5 text-xs text-hc-warning">
          Save a price to publish this program and enable sharing.
        </p>
      )}

      <label className="block text-xs text-hc-text-dim">
        Price (CAD, before tax)
        <input
          type="number"
          min="0.5"
          step="0.01"
          value={priceDollars}
          onChange={(e) => setPriceDollars(e.target.value)}
          className="mt-1 w-full rounded-[10px] border border-hc-surface-border bg-hc-surface-strong px-3 py-2 text-sm text-hc-text"
          placeholder="49.99"
        />
      </label>

      <fieldset className="space-y-2">
        <legend className="text-xs text-hc-text-dim">Pricing mode</legend>
        <label className="flex items-center gap-2 text-sm text-hc-text">
          <input
            type="radio"
            name={`pricingMode-${listing.id}`}
            checked={pricingMode === "oneTime"}
            onChange={() => setPricingMode("oneTime")}
          />
          One-time purchase
        </label>
        <label className="flex items-center gap-2 text-sm text-hc-text">
          <input
            type="radio"
            name={`pricingMode-${listing.id}`}
            checked={pricingMode === "subscription"}
            onChange={() => setPricingMode("subscription")}
          />
          Monthly subscription
        </label>
      </fieldset>

      {!connectReady && (
        <p className="text-xs text-hc-text-dim">
          Complete{" "}
          <Link to="/billing" className="text-hc-text-muted hover:text-hc-text transition">
            payout setup
          </Link>{" "}
          before clients can purchase.
        </p>
      )}

      {error && <p className="text-xs text-hc-error">{error}</p>}
      {saved && (
        <p className="text-xs text-hc-success">Price saved — your program is live and ready to share.</p>
      )}

      <button
        type="submit"
        disabled={saving}
        className="flex w-full items-center justify-center rounded-[14px] px-4 py-3 text-sm font-semibold text-white transition hc-brand-gradient hc-shadow-brand hover:brightness-110 active:scale-95 disabled:opacity-60"
      >
        {saving ? "Saving…" : "Save price"}
      </button>
    </form>
  );
}
